// views/Profile/components/AgentBuilder.jsx
//
// "Build your agent" — the primary estimator on the Pricing page. The
// user assembles an agent (intelligence tier + integrations + extra
// actions) and picks a monthly volume; a sticky live panel shows the
// per-task cost, the monthly total, and which membership plan fits.
//
// Everything is computed CLIENT-SIDE from live data:
//   - per-action prices  → usePricingCatalog (passed in as `catalog`)
//   - plan allowances    → useTiers (GET /tiers)
// so the numbers can never drift from the Prices tab or the plan catalog.
import React, { useMemo, useState } from 'react';
import {
  Brain,
  Check,
  Sparkles,
  Shield,
  CheckCircle2,
  Bookmark,
  Workflow,
  Database,
  BookOpen,
  Image as ImageIcon,
  Headset,
  // BrandLogo fallbacks
  MessageCircle,
  Instagram as InstagramIcon,
  Mail,
  ShoppingBag,
  Facebook,
  HardDrive,
  FileSpreadsheet,
  Calendar,
  Globe,
} from 'lucide-react';
import useTiers from '../../../hooks/billing/useTiers';
import './AgentBuilder.css';

// Knowledge-base retrieval price — mirrors RAG_RETRIEVAL published on the
// Prices tab so the two surfaces never disagree.
const KB_QUERY_COINS = 2;

const STORAGE_KEY = 'zenit:agent-estimate';

// Read a previously saved estimate (if any) so "Save estimate" actually
// restores the user's setup when they reopen the builder.
function loadSavedEstimate() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// Monthly-volume slider snaps to these ticks (matches the plan ladder's
// useful range without giving a false sense of precision).
const VOLUME_STEPS = [100, 500, 1000, 2000, 5000, 10000];

// ---------------------------------------------------------------------------
// Brand logos — remote CDN images (the pattern already used across the app)
// with a lucide fallback if the network image fails.
// ---------------------------------------------------------------------------

const BRAND = {
  whatsapp: {
    label: 'WhatsApp',
    url: 'https://cdn.simpleicons.org/whatsapp/25D366',
    Fallback: MessageCircle,
  },
  instagram: {
    label: 'Instagram',
    url: 'https://cdn.simpleicons.org/instagram/E4405F',
    Fallback: InstagramIcon,
  },
  gmail: {
    label: 'Gmail',
    url: 'https://ssl.gstatic.com/images/branding/product/1x/gmail_2020q4_48dp.png',
    Fallback: Mail,
  },
  google: {
    label: 'Google Tools',
    url: 'https://cdn.simpleicons.org/google/4285F4',
    Fallback: Globe,
  },
  shopify: {
    label: 'Shopify',
    url: 'https://cdn.simpleicons.org/shopify/95BF47',
    Fallback: ShoppingBag,
  },
  meta: {
    label: 'Meta',
    url: 'https://cdn.simpleicons.org/meta/0467DF',
    Fallback: Facebook,
  },
  facebook: {
    label: 'Facebook',
    url: 'https://cdn.simpleicons.org/facebook/1877F2',
    Fallback: Facebook,
  },
  drive: {
    label: 'Google Drive',
    url: 'https://ssl.gstatic.com/images/branding/product/1x/drive_2020q4_48dp.png',
    Fallback: HardDrive,
  },
  sheets: {
    label: 'Google Sheets',
    url: 'https://ssl.gstatic.com/images/branding/product/1x/sheets_2020q4_48dp.png',
    Fallback: FileSpreadsheet,
  },
  calendar: {
    label: 'Google Calendar',
    url: 'https://ssl.gstatic.com/images/branding/product/1x/calendar_2020q4_48dp.png',
    Fallback: Calendar,
  },
};

function BrandLogo({ id, size = 20 }) {
  const [failed, setFailed] = useState(false);
  const meta = BRAND[id];
  if (!meta) return null;
  if (failed || !meta.url) {
    const F = meta.Fallback;
    return <F size={size} aria-hidden="true" />;
  }
  return (
    <img
      src={meta.url}
      alt={meta.label}
      width={size}
      height={size}
      style={{ objectFit: 'contain', display: 'block' }}
      onError={() => setFailed(true)}
    />
  );
}

// Selectable integrations (order matches the mock; all rendered with a
// real brand logo).
const INTEGRATIONS = [
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'gmail', label: 'Gmail' },
  { id: 'google', label: 'Google Tools' },
  { id: 'shopify', label: 'Shopify' },
  { id: 'meta', label: 'Meta' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'drive', label: 'Drive' },
  { id: 'sheets', label: 'Sheets' },
  { id: 'calendar', label: 'Calendar' },
];

// Intelligence-tier display names keyed by the tier's coin cost, so the
// builder reads Basic/Intermediate/Advanced regardless of the backend's
// internal tier labels.
const INTELLIGENCE_BY_COINS = {
  1: { name: 'Basic', sub: 'Simple, fast tasks' },
  3: { name: 'Intermediate', sub: 'Balanced reasoning' },
  5: { name: 'Advanced', sub: 'Complex reasoning' },
  10: { name: 'Max', sub: 'Highest capability' },
};

const fmt = (n) => new Intl.NumberFormat('en-US').format(n);

function planPriceLabel(t) {
  if (t.displayPrice) return t.displayPrice;
  if (t.monthlyPriceUsd == null) return 'Custom';
  if (t.monthlyPriceUsd === 0) return 'Free';
  const isInt = Number.isInteger(t.monthlyPriceUsd);
  return `$${t.monthlyPriceUsd.toLocaleString('en-US', {
    minimumFractionDigits: isInt ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}

// ===========================================================================
// AgentBuilder
// ===========================================================================

export default function AgentBuilder({ catalog }) {
  const { data: tiers, loading: tiersLoading } = useTiers();

  const resources = catalog?.resources ?? {};
  const assistantTiers = useMemo(
    () =>
      [...(catalog?.assistantTiers ?? [])].sort(
        (a, b) => (a.coins ?? 0) - (b.coins ?? 0)
      ),
    [catalog]
  );
  const cheapestImage = useMemo(
    () =>
      [...(catalog?.imageModels ?? [])].sort(
        (a, b) => (a.coinsPerImage ?? Infinity) - (b.coinsPerImage ?? Infinity)
      )[0] ?? null,
    [catalog]
  );

  const wfCoins = resources.workflow_execution?.coins ?? 1;
  const integCoins = resources.integration_call?.coins ?? 1;
  const dbWriteCoins = resources.database_write?.coins ?? 1;
  const imageCoins = cheapestImage?.coinsPerImage ?? 65;

  // ---- state (hydrated from a saved estimate when present) ----
  const initial = useMemo(loadSavedEstimate, []);
  const defaultTier =
    assistantTiers.find((t) => t.coins === 3) ??
    assistantTiers[Math.floor(assistantTiers.length / 2)] ??
    null;
  const [tierCoinsSel, setTierCoinsSel] = useState(
    initial?.tierCoins ?? defaultTier?.coins ?? 3
  );
  const [selected, setSelected] = useState(
    () => new Set(initial?.integrations ?? ['instagram', 'google', 'shopify'])
  );
  const [extras, setExtras] = useState(
    initial?.extras ?? {
      workflow: true,
      knowledge: false,
      database: true,
      image: false,
    }
  );
  const [volumeIdx, setVolumeIdx] = useState(initial?.volumeIdx ?? 1); // 500
  const [saved, setSaved] = useState(false);

  const activeTier =
    assistantTiers.find((t) => t.coins === tierCoinsSel) ?? defaultTier;
  const tierName =
    INTELLIGENCE_BY_COINS[activeTier?.coins]?.name ??
    activeTier?.label ??
    'Agent';
  const selectedList = INTEGRATIONS.filter((i) => selected.has(i.id));
  const executions = VOLUME_STEPS[volumeIdx];

  const toggleIntegration = (id) => {
    setSaved(false);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleExtra = (key) => {
    setSaved(false);
    setExtras((e) => ({ ...e, [key]: !e[key] }));
  };

  // ---- cost breakdown per task ----
  const lines = [];
  if (extras.workflow) {
    lines.push({ icon: <Workflow size={15} />, label: 'Workflow run', coins: wfCoins });
  }
  lines.push({
    icon: <Headset size={15} />,
    label: `Agent call (${tierName})`,
    coins: activeTier?.coins ?? 0,
  });
  if (selectedList.length > 0) {
    lines.push({
      icon: <Sparkles size={15} />,
      label: `Integrations used (${selectedList.length})`,
      coins: selectedList.length * integCoins,
    });
  }
  if (extras.knowledge) {
    lines.push({
      icon: <BookOpen size={15} />,
      label: 'Knowledge base query',
      coins: KB_QUERY_COINS,
    });
  }
  if (extras.database) {
    lines.push({
      icon: <Database size={15} />,
      label: 'Database write',
      coins: dbWriteCoins,
    });
  }
  if (extras.image) {
    lines.push({
      icon: <ImageIcon size={15} />,
      label: 'Image generation',
      coins: imageCoins,
    });
  }
  const perTask = lines.reduce((sum, l) => sum + (l.coins ?? 0), 0);
  const monthly = perTask * executions;

  // ---- plan fit ----
  const payablePlans = useMemo(
    () =>
      (tiers ?? [])
        .filter((t) => t.type !== 'ENTERPRISE' && t.monthlyCoinAllowance != null)
        .sort((a, b) => a.monthlyCoinAllowance - b.monthlyCoinAllowance),
    [tiers]
  );
  const enterprise = (tiers ?? []).find((t) => t.type === 'ENTERPRISE') ?? null;
  const fitPlan = payablePlans.find((t) => t.monthlyCoinAllowance >= monthly);
  const remaining = fitPlan ? fitPlan.monthlyCoinAllowance - monthly : null;

  const handleSave = () => {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          tierCoins: tierCoinsSel,
          integrations: [...selected],
          extras,
          volumeIdx,
          executions,
          perTask,
          monthly,
          plan: fitPlan?.type ?? enterprise?.type ?? null,
        })
      );
      setSaved(true);
    } catch {
      setSaved(true); // storage may be unavailable; still confirm to the user
    }
  };

  // ---- extra-action toggles config ----
  const extraRows = [
    {
      key: 'workflow',
      icon: <Workflow size={17} />,
      label: 'Workflow run',
      sub: `${wfCoins} coin per task`,
    },
    {
      key: 'knowledge',
      icon: <BookOpen size={17} />,
      label: 'Knowledge base query',
      sub: `${KB_QUERY_COINS} coins per query`,
    },
    {
      key: 'database',
      icon: <Database size={17} />,
      label: 'Database write',
      sub: `${dbWriteCoins} coin when used`,
    },
    {
      key: 'image',
      icon: <ImageIcon size={17} />,
      label: 'Image generation',
      sub: extras.image ? `${imageCoins} coins per image` : 'Not included',
    },
  ];

  return (
    <div className="agb">
      <div className="agb-intro">
        <h2 className="agb-title">Build your agent estimate</h2>
        <p className="agb-subtitle">
          Choose how your agent works and see the full cost instantly.
        </p>
      </div>

      <div className="agb-layout">
        {/* ============ LEFT — configuration ============ */}
        <div className="agb-config">
          {/* 1. Intelligence */}
          <section className="agb-card">
            <div className="agb-card-head">
              <span className="agb-step">1</span>
              <h3 className="agb-card-title">Choose intelligence</h3>
            </div>
            <div className="agb-intel-grid">
              {assistantTiers.map((t) => {
                const meta = INTELLIGENCE_BY_COINS[t.coins] ?? {
                  name: t.label ?? `Tier ${t.tier}`,
                  sub: '',
                };
                const active = t.coins === tierCoinsSel;
                return (
                  <button
                    key={t.tier}
                    type="button"
                    className={`agb-intel ${active ? 'agb-intel--active' : ''}`}
                    onClick={() => {
                      setSaved(false);
                      setTierCoinsSel(t.coins);
                    }}
                  >
                    {active && (
                      <span className="agb-intel-check">
                        <Check size={13} />
                      </span>
                    )}
                    <span className="agb-intel-icon">
                      <Brain size={22} />
                    </span>
                    <span className="agb-intel-name">{meta.name}</span>
                    <span className="agb-intel-price">
                      {t.coins} {t.coins === 1 ? 'coin' : 'coins'} / call
                    </span>
                    {meta.sub && <span className="agb-intel-sub">{meta.sub}</span>}
                  </button>
                );
              })}
            </div>
          </section>

          {/* 2. Integrations */}
          <section className="agb-card">
            <div className="agb-card-head">
              <span className="agb-step">2</span>
              <h3 className="agb-card-title">How will you use your agent?</h3>
            </div>
            <p className="agb-card-hint">
              Select the tools and integrations your agent will use.
            </p>
            <div className="agb-integrations">
              {INTEGRATIONS.map((i) => {
                const active = selected.has(i.id);
                return (
                  <button
                    key={i.id}
                    type="button"
                    className={`agb-int ${active ? 'agb-int--active' : ''}`}
                    onClick={() => toggleIntegration(i.id)}
                  >
                    <span className="agb-int-logo">
                      <BrandLogo id={i.id} size={22} />
                    </span>
                    <span className="agb-int-label">{i.label}</span>
                    <span className={`agb-check ${active ? 'agb-check--on' : ''}`}>
                      {active && <Check size={12} />}
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="agb-multi">
              <Sparkles size={13} /> Multiple selections allowed.
            </p>
          </section>

          {/* 3. Extra actions */}
          <section className="agb-card">
            <div className="agb-card-head">
              <span className="agb-step">3</span>
              <h3 className="agb-card-title">Extra actions</h3>
            </div>
            <div className="agb-extras">
              {extraRows.map((r) => (
                <div key={r.key} className="agb-extra">
                  <span className="agb-extra-icon">{r.icon}</span>
                  <span className="agb-extra-text">
                    <span className="agb-extra-label">{r.label}</span>
                    <span className="agb-extra-sub">{r.sub}</span>
                  </span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={extras[r.key]}
                    aria-label={r.label}
                    className={`agb-switch ${
                      extras[r.key] ? 'agb-switch--on' : ''
                    }`}
                    onClick={() => toggleExtra(r.key)}
                  >
                    <span className="agb-switch-knob" />
                  </button>
                </div>
              ))}
            </div>
          </section>

          {/* 4. Monthly volume */}
          <section className="agb-card">
            <div className="agb-card-head">
              <span className="agb-step">4</span>
              <h3 className="agb-card-title">Monthly task executions</h3>
            </div>
            <p className="agb-card-hint">
              Estimate how many tasks your agent will complete each month.
            </p>
            <div className="agb-volume-value">
              <span className="agb-volume-num">{fmt(executions)}</span>
              <span className="agb-volume-unit">task executions / month</span>
            </div>
            <input
              type="range"
              className="agb-slider"
              min={0}
              max={VOLUME_STEPS.length - 1}
              step={1}
              value={volumeIdx}
              onChange={(e) => {
                setSaved(false);
                setVolumeIdx(Number(e.target.value));
              }}
            />
            <div className="agb-slider-ticks">
              {VOLUME_STEPS.map((s) => (
                <span key={s}>{s >= 1000 ? `${s / 1000}K` : s}</span>
              ))}
            </div>
          </section>
        </div>

        {/* ============ RIGHT — live estimate ============ */}
        <aside className="agb-estimate">
          <div className="agb-estimate-inner">
            <h3 className="agb-est-title">
              <Sparkles size={16} /> Live estimate
            </h3>

            <div className="agb-panel">
              <span className="agb-panel-title">Cost breakdown per task</span>
              <div className="agb-breakdown">
                {lines.map((l, idx) => (
                  <div key={idx} className="agb-bd-row">
                    <span className="agb-bd-label">
                      <span className="agb-bd-icon">{l.icon}</span>
                      {l.label}
                    </span>
                    <span className="agb-bd-dots" aria-hidden />
                    <span className="agb-bd-coins">
                      {l.coins} {l.coins === 1 ? 'coin' : 'coins'}
                    </span>
                  </div>
                ))}
              </div>
              <div className="agb-pertask">
                <span>Total per task</span>
                <span className="agb-pertask-val">
                  {fmt(perTask)} {perTask === 1 ? 'coin' : 'coins'}
                </span>
              </div>
            </div>

            <div className="agb-panel agb-panel--total">
              <span className="agb-total-formula">
                {fmt(executions)} task executions × {fmt(perTask)} coins
              </span>
              <div className="agb-total-num">{fmt(monthly)}</div>
              <span className="agb-total-unit">coins / month</span>

              {tiersLoading ? (
                <div className="agb-fit agb-fit--loading">Checking plans…</div>
              ) : fitPlan ? (
                <>
                  <div className="agb-fit">
                    <Shield size={16} />
                    <span>
                      Fits in <strong>{fitPlan.displayName}</strong> plan
                      <span className="agb-fit-sub">
                        {' '}
                        ({fmt(fitPlan.monthlyCoinAllowance)} coins / month ·{' '}
                        {planPriceLabel(fitPlan)})
                      </span>
                    </span>
                  </div>
                  <div className="agb-remaining">
                    <CheckCircle2 size={15} />
                    Estimated remaining balance:{' '}
                    <strong>{fmt(remaining)} coins</strong>
                  </div>
                </>
              ) : (
                <div className="agb-fit agb-fit--enterprise">
                  <Shield size={16} />
                  <span>
                    Beyond our standard plans — talk to us about{' '}
                    <strong>{enterprise?.displayName ?? 'Enterprise'}</strong>{' '}
                    (custom allowance).
                  </span>
                </div>
              )}
            </div>

            <ul className="agb-reassure">
              <li>
                <Sparkles size={14} /> Great for customer support, lead routing
                and order updates.
              </li>
              <li>
                <CheckCircle2 size={14} /> No hidden fees.
              </li>
              <li>
                <CheckCircle2 size={14} /> You only pay for the actions used.
              </li>
            </ul>

            <div className="agb-actions">
              <button
                type="button"
                className={`agb-save ${saved ? 'agb-save--done' : ''}`}
                onClick={handleSave}
              >
                {saved ? <Check size={16} /> : <Bookmark size={16} />}
                {saved ? 'Saved' : 'Save estimate'}
              </button>
            </div>

            <p className="agb-disclaimer">
              This is a reference estimate. Your final cost may vary depending on
              the number of connections and database operations your workflow
              actually runs. Prices may change — see the FAQ tab for details.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
